import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, TextField } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useState } from "react";

import { useAccountMembers } from "api/services/useAccountMembers";
import { TooltipModalButton } from "components/common/TooltipModalButton";
import { Modal } from "new-components/modals/Modal";
import { Typography } from "new-components/typography/Typography";

interface AddMemberModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const tooltipRoleContentText = `
**Collaborator**

Collaborators have access to all elements of the account except for account billing information.

&nbsp;

**Labeler**

Labelers only have access to the review page. They can view and submit answers to image queries in the queue but can not view or edit any other aspects of the account.
`;

export function AddMemberModal({ open, onClose, onSuccess }: AddMemberModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("collaborator");
  const [emailError, setEmailError] = useState("");
  const [postError, setPostError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const { createAccountMember, isCreating: isSubmitting } = useAccountMembers();

  const validateEmail = (email: string) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setEmail(value);
    if (value && !validateEmail(value)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const handleRoleChange = (e: SelectChangeEvent) => {
    setRole(e.target.value);
  };

  const handleSubmit = async () => {
    if (!email || emailError) {
      return;
    }

    createAccountMember(
      { email, role },
      {
        onSuccess: (responseData) => {
          setSuccessMessage(
            `The unique username for this account has been set to <b>${responseData?.created_email}</b> and an invitation email has been sent.`
          );
          setEmail("");
          setPostError("");
          setRole("collaborator");
          enqueueSnackbar("Member added successfully", { variant: "success" });
          onSuccess();
        },
        onError: (apiError) => {
          const errorMessage = apiError?.data?.error;
          console.error("Error adding member:", errorMessage);

          setPostError(errorMessage || "An error occurred while adding the member. Please try again.");
        },
      }
    );
  };

  const handleClose = () => {
    onClose();
    setEmail("");
    setPostError("");
    if (successMessage) {
      // This to avoid changing too quickly the modal content when closing and showing the previous content
      setTimeout(() => setSuccessMessage(""), 500);
    }
  };

  const modalBody = () => {
    if (successMessage) {
      return (
        <Box>
          <Typography textAlign="center" dangerouslySetInnerHTML={{ __html: successMessage }} />
        </Box>
      );
    }

    return (
      <Stack width="100%">
        <Stack direction={{ xs: "column", md: "row" }} id="add-member-modal-content" spacing={2} width="100%">
          <Box my={2} width="100%">
            <InputLabel className="text-sm md:text-base text-black font-normal mb-4">Email:</InputLabel>

            <TextField
              autoFocus
              type="email"
              fullWidth
              value={email}
              onChange={handleEmailChange}
              error={!!emailError}
              helperText={emailError}
              disabled={isSubmitting}
              required
              placeholder="email"
            />
          </Box>
          <Box width="100%">
            <InputLabel className="text-sm md:text-base text-black font-normal mb-2">
              Role:
              <TooltipModalButton
                modalTitle="User Roles"
                modalContent={tooltipRoleContentText}
                color="black"
                size="medium"
                tooltipSize="xs"
              />
            </InputLabel>

            <FormControl fullWidth disabled={isSubmitting} size="medium">
              <Select
                labelId="role-select-label"
                value={role}
                onChange={handleRoleChange}
                sx={{ paddingY: 1 }}
                data-testid="role-select"
              >
                <MenuItem value="collaborator">Collaborator</MenuItem>
                <MenuItem value="labeler">Labeler</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Stack>
        {postError && (
          <Typography color="error" mt={1}>
            {postError || "An error occurred while adding the member. Please try again."}
          </Typography>
        )}
      </Stack>
    );
  };

  return (
    <Modal
      size="small"
      open={open}
      onClose={handleClose}
      onCancel={handleClose}
      onConfirmation={successMessage ? undefined : handleSubmit}
      body={modalBody()}
      confirmationButtonTitle={successMessage ? "" : "Send invite"}
      cancelButtonTitle={successMessage ? "Close" : "Cancel"}
      title="Add user"
      isConfirmationLoading={isSubmitting}
      isConfirmationDisabled={!email || !!emailError || isSubmitting}
      isCancelDisabled={isSubmitting}
    />
  );
}
